import * as httpx from "httpx"
import * as api from "brandguard/api"
import { CancellablePromise } from "real-cancellable-promise"

export type UndoableAssetType = "image" | "text" | "document" | "video"

type Item =
	| api.ImageSearchResponseItem
	| api.TextSearchResponseItem
	| api.DocumentSearchResponseItem
	| api.VideoSearchResponseItem

// undoApproval reverts an approved/rejected asset back to the review queue by
// re-opening the upload row via POST /review (un-tombstones + sets observation
// to INREVIEW server-side) and soft-deleting the approvallog audit row.
// The upload and approvallog share an id so both paths address the same row.
export function undoApproval(eventType: UndoableAssetType, item: Item, bearer: httpx.option): CancellablePromise<void> {
	if (!item.event?.id || !item.event?.brand_id) {
		return CancellablePromise.reject(new Error("cannot undo: item has no event"))
	}
	const id = item.event.id
	const brandID = item.event.brand_id

	const reopenUpload = (): Promise<unknown> => {
		switch (eventType) {
			case "image":
				return api.images.uploads.review(id, brandID, bearer)
			case "text":
				return api.text.uploads.review(id, brandID, bearer)
			case "document":
				return api.documents.uploads.review(id, brandID, bearer)
			case "video":
				return api.videos.uploads.review(id, brandID, bearer)
		}
	}

	const destroyApprovalLog = (): Promise<unknown> => {
		switch (eventType) {
			case "image":
				return api.images.approvallog.destroy(brandID, id, bearer)
			case "text":
				return api.text.approvallog.destroy(brandID, id, bearer)
			case "document":
				return api.documents.approvallog.destroy(brandID, id, bearer)
			case "video":
				return api.videos.approvallog.destroy(brandID, id, bearer)
		}
	}

	return new CancellablePromise<void>(
		reopenUpload()
			.then(() => destroyApprovalLog())
			.catch(httpx.errors.notFound((cause) => console.debug("approvallog already gone", cause)))
			.then(() => undefined),
		() => undefined,
	)
}
